import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, ShieldAlert, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";

const fmtDate = (iso, lang) => { try { return new Date(iso).toLocaleString(lang || "fr", { dateStyle: "long", timeStyle: "short" }); } catch { return iso || ""; } };

// Lot 604 — appareil ENCORE connecté : une demande de récupération vise mon compte → « C'est moi » (application immédiate,
// toutes les sessions se ferment) ou « Ce n'est pas moi » (annulation). Rendue sur l'accueil et dans Paramètres › Mon compte.
export const PendingRecoveryCard = ({ compact = false }) => {
  const { t, lang, logout } = useApp();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    let alive = true;
    api.get("/auth/account/recovery/pending").then(({ data }) => { if (alive) setItems(data.items || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!items.length) return null;

  const confirm = async (rec) => {
    if (!window.confirm(t("recovery_card.confirm_prompt", "Confirmer : ton compte sera rattaché à {new} tout de suite et toutes tes sessions seront fermées. Tu te reconnecteras avec ce nouvel identifiant.").replace("{new}", rec.new_hint))) return;
    setBusy(rec.id);
    try {
      const { data } = await api.post(`/auth/account/recovery/${rec.id}/confirm`);
      if (data.ok) {
        toast.success(t("recovery_card.applied", "Compte rattaché à {new} ✓ — reconnecte-toi avec ce nouvel identifiant.").replace("{new}", data.new_value || rec.new_hint), { duration: 8000 });
        await logout();
        nav("/", { replace: true });
      } else toast.error(t("recovery_card.not_applied", "Cette demande n'est plus applicable."));
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setBusy(""); }
  };
  const cancel = async (rec) => {
    setBusy(rec.id);
    try {
      await api.post(`/auth/account/recovery/${rec.id}/cancel`);
      setItems((l) => l.filter((x) => x.id !== rec.id));
      toast.success(t("recovery_card.cancelled", "Demande annulée. Par précaution, change ton mot de passe."), { duration: 8000 });
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-3" data-testid="pending-recovery-list">
      {items.map((rec) => (
        <div key={rec.id} className={`yalola-card ${compact ? "p-4" : "p-4 animate-in fade-in slide-in-from-top-2 duration-500"}`} style={{ border: "1px solid #F59E0B" }} data-testid={`pending-recovery-${rec.id}`}>
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 shrink-0 rounded-full grid place-items-center" style={{ background: "#FEF3C7", color: "#92400E" }}><KeyRound size={20} /></div>
            <div className="min-w-0 flex-1">
              <div className="font-bold font-display" data-testid="pending-recovery-title">{t("recovery_card.title", "Quelqu'un demande à rattacher ton compte à {new}").replace("{new}", rec.new_hint)}</div>
              <p className="text-sm mt-0.5" style={{ color: "var(--yalola-text-muted)" }} data-testid="pending-recovery-desc">
                {rec.status === "pending"
                  ? t("recovery_card.desc_pending", "Si tu n'as plus accès à ton ancien identifiant et que c'est toi, confirme : le changement s'applique tout de suite. Sinon, annule. Sans réponse, il s'appliquera automatiquement le {when}.").replace("{when}", fmtDate(rec.apply_at, lang))
                  : t("recovery_card.desc_manual", "Un administrateur doit valider cette demande. Si ce n'est pas toi, annule-la maintenant.")}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <Button size="sm" className="rounded-full pill-primary" onClick={() => confirm(rec)} disabled={!!busy} data-testid="pending-recovery-confirm">
                  {busy === rec.id ? <Loader2 size={14} className="animate-spin" /> : t("recovery_card.confirm", "C'est moi — confirmer")}
                </Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => cancel(rec)} disabled={!!busy} data-testid="pending-recovery-cancel">
                  <ShieldAlert size={14} className="mr-1" /> {t("recovery_card.cancel", "Ce n'est pas moi — annuler")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PendingRecoveryCard;
